import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Form, ListGroup, Button, Badge, OverlayTrigger, Tooltip, Row, Col, Alert } from 'react-bootstrap';
import { AlertTriangle, Unlock, CheckCircle, Ban } from 'lucide-react';
import { getBases, getRecommendedDrivers, apiClient } from '../api';
import useAuthStore from '../../store/useAuthStore';

const DriverAssignModal = ({ show, onHide, drivers, vehicles, onAssign, baseId, allTrips, currentTrip }) => {
    const { user } = useAuthStore();
    const userRole = user?.role?.toLowerCase();
    const isProvider = userRole === 'provider';
    const providerId = user?.linked_provider_id;

    const [searchTerm, setSearchTerm] = useState('');
    const [bases, setBases] = useState([]);
    const [recommendedDrivers, setRecommendedDrivers] = useState([]);
    const [serverConflictsByDriver, setServerConflictsByDriver] = useState({});
    const [changeBaseFilter, setChangeBaseFilter] = useState(false);
    const [selectedFilterBaseId, setSelectedFilterBaseId] = useState('');
    const [allowExceptional, setAllowExceptional] = useState(false);
    const [exceptionalPromptDriver, setExceptionalPromptDriver] = useState(null);

    useEffect(() => {
        if (show) {
            const loadBases = async () => {
                try {
                    const data = await getBases();
                    setBases(data);
                } catch (e) {
                    console.error("Error al cargar bases en modal de asignación:", e);
                }
            };
            loadBases();
            setChangeBaseFilter(false);
            setSelectedFilterBaseId(baseId || '');
            setAllowExceptional(false);
            setSearchTerm('');
            setExceptionalPromptDriver(null);
            setServerConflictsByDriver({});

            if (currentTrip?.scheduled_time) {
                apiClient.post('transport/trips/check-driver-conflict', {
                    trip_id: currentTrip.id || null,
                    scheduled_time: currentTrip.scheduled_time,
                    estimated_duration_minutes: parseInt(currentTrip.estimated_duration_minutes) || 30,
                    origin_base_id: baseId || currentTrip.origin_base_id || null,
                    timezone: currentTrip.origin_base?.timezone || currentTrip.timezone || null,
                    flight_number: currentTrip.flight_number || null,
                    pickup_address: currentTrip.pickup_address || null,
                    pickup_lat: currentTrip.pickup_lat ? parseFloat(currentTrip.pickup_lat) : null,
                    pickup_lng: currentTrip.pickup_lng ? parseFloat(currentTrip.pickup_lng) : null,
                    dropoff_lat: currentTrip.dropoff_lat ? parseFloat(currentTrip.dropoff_lat) : null,
                    dropoff_lng: currentTrip.dropoff_lng ? parseFloat(currentTrip.dropoff_lng) : null,
                    check_all_drivers: true,
                }).then(res => {
                    if (res.data?.conflicts_by_driver) {
                        setServerConflictsByDriver(res.data.conflicts_by_driver);
                    }
                }).catch(err => {
                    console.warn("⚠️ Error consultando conflictos globales de agenda en modal rápido:", err);
                });
            }

            // Cargar recomendaciones de equidad para viajes en Santiago (base 1)
            const activeBase = baseId || currentTrip?.origin_base_id || 1;
            if (Number(activeBase) === 1) {
                getRecommendedDrivers({
                    base_id: 1,
                    scheduled_time: currentTrip?.scheduled_time || null
                }).then(res => {
                    if (res) {
                        setRecommendedDrivers(res.all_recommendations || []);
                    }
                }).catch(e => {
                    console.warn("⚠️ Error al cargar recomendaciones de equidad en modal rápido:", e);
                });
            } else {
                setRecommendedDrivers([]);
            }
        } else {
            setRecommendedDrivers([]);
            setServerConflictsByDriver({});
        }
    }, [show, baseId, currentTrip]);

    const driverConflictsMap = useMemo(() => {
        const map = new Map();
        if (!currentTrip?.scheduled_time) return map;

        // 1. Conflictos reales consultados directo a la BD (±24h, inmune a filtros de pantalla)
        Object.entries(serverConflictsByDriver || {}).forEach(([drvIdStr, confList]) => {
            const dId = parseInt(drvIdStr, 10);
            if (!isNaN(dId) && Array.isArray(confList) && confList.length > 0) {
                map.set(dId, confList);
            }
        });

        // 2. Respaldo instantáneo local con allTrips mientras responde el servidor
        if (allTrips && allTrips.length > 0) {
            const startTime = new Date(currentTrip.scheduled_time).getTime();
            const duration = Math.max(40, parseInt(currentTrip.estimated_duration_minutes) || 40) * 60000;
            const endTime = startTime + duration + (20 * 60000);

            allTrips.forEach(t => {
                if (!t.driver_id || t.id === currentTrip.id) return;
                const st = (t.status || '').toLowerCase();
                if (['completed', 'cancelled', 'cancelled_paid', 'anulado', 'no_show', 'draft'].includes(st)) return;

                const tStart = new Date(t.scheduled_time).getTime();
                const tDuration = Math.max(40, parseInt(t.estimated_duration_minutes) || 40) * 60000;
                const tEnd = tStart + tDuration + (20 * 60000);

                if (startTime < tEnd && endTime > tStart) {
                    if (!map.has(t.driver_id)) {
                        const dt = new Date(t.scheduled_time);
                        const hhmm = dt.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
                        const diffMins = Math.round(Math.abs(startTime - tStart) / 60000);
                        map.set(t.driver_id, [{
                            trip_id: t.id,
                            passenger_name: t.passenger_name,
                            scheduled_time: hhmm,
                            diff_minutes: diffMins,
                            conflict_reason: `Reserva #${t.id} programada a las ${hhmm} (diferencia: ${diffMins} min).`
                        }]);
                    }
                }
            });
        }

        return map;
    }, [serverConflictsByDriver, allTrips, currentTrip]);

    // Evaluar estado y restricciones de cada conductor para este viaje
    const evaluatedDrivers = useMemo(() => {
        const activeBaseId = changeBaseFilter ? selectedFilterBaseId : baseId;
        const reqVehicleType = currentTrip?.requested_vehicle_type;
        const requiredPax = Math.max(
            reqVehicleType?.capacity || 1,
            parseInt(currentTrip?.passenger_count || 1)
        );
        const tripDate = currentTrip?.scheduled_time ? new Date(currentTrip.scheduled_time) : null;
        const tripDateStr = currentTrip?.scheduled_time ? currentTrip.scheduled_time.split('T')[0] : '';

        // ASISTENTE DE EQUIDAD: Categoría relevante para sugerencias
        const reqVTName = currentTrip?.requested_vehicle_type?.name?.toUpperCase()?.trim() || '';
        let targetCategory = null;
        if (['STANDARD', 'EXECUTIVE', 'LUXURY'].includes(reqVTName)) {
            targetCategory = 'standard';
        } else if (['PEOPLE_CARRIER', 'LARGE_PEOPLE_CARRIER'].includes(reqVTName)) {
            targetCategory = 'van';
        }

        const relevantRecs = targetCategory
            ? (recommendedDrivers || []).filter(r => r.category === targetCategory)
            : (recommendedDrivers || []);

        let myVehicles = vehicles || [];
        let myDrivers = drivers || [];
        if (isProvider && providerId) {
            myVehicles = myVehicles.filter(v => v.provider_id === providerId);
            myDrivers = myDrivers.filter(d => d.provider_id === providerId);
        }

        return myDrivers.filter(d => d.is_active).map(d => {
            const v = myVehicles.find(veh => veh.current_driver_id === d.id);
            const reasons = [];
            let hasCapacityBlock = false;
            let capacityDetail = '';

            // 1. Móvil asignado
            if (!v) {
                reasons.push("Sin móvil asignado");
            } else {
                if (!v.is_active) reasons.push("Móvil inactivo");
                
                // Base
                if (activeBaseId) {
                    const hasBase = v.permitted_bases?.some(b => String(b.id) === String(activeBaseId));
                    if (!hasBase) reasons.push("Móvil de otra base");
                }

                // Capacidad de pasajeros (BLOQUEO FÍSICO DURO - NO SE PUEDE FORZAR)
                const vehicleCapacity = v.vehicle_type?.capacity || 4;
                if (vehicleCapacity < requiredPax) {
                    hasCapacityBlock = true;
                    capacityDetail = `${requiredPax} pax req vs ${vehicleCapacity} móvil`;
                    reasons.push(`Capacidad insuficiente (${capacityDetail})`);
                }

                // Documentos del vehículo
                if (tripDate && v.technical_revision_expiry) {
                    const revExp = new Date(v.technical_revision_expiry + 'T23:59:59');
                    if (revExp < tripDate) {
                        reasons.push("Revisión Técnica del móvil vencida");
                    }
                }
                if (tripDate && v.soap_expiry) {
                    const soapExp = new Date(v.soap_expiry + 'T23:59:59');
                    if (soapExp < tripDate) {
                        reasons.push("SOAP del móvil vencido");
                    }
                }
            }

            // 2. Sesión activa en la app
            if (!d.has_active_session) {
                reasons.push("Sin sesión activa en la app");
            }

            // 3. Licencia de conducir
            if (tripDate && d.license_expiry) {
                const licExp = new Date(d.license_expiry + 'T23:59:59');
                if (licExp < tripDate) {
                    const [y, m, day] = d.license_expiry.split('-');
                    reasons.push(`Licencia de conducir vencida (Venció ${day}/${m}/${y})`);
                }
            }

            // 4. Ausencias / Vacaciones
            if (tripDateStr && Array.isArray(d.unavailabilities)) {
                for (const u of d.unavailabilities) {
                    if (u.start_date <= tripDateStr && tripDateStr <= u.end_date) {
                        reasons.push(`De ${u.reason || 'Ausencia'} (${u.start_date} al ${u.end_date})`);
                    }
                }
            }

            // 5. Restricción Solo Booking
            let hasBookingOnlyBlock = false;
            if (d.only_booking && currentTrip?.client_id && parseInt(currentTrip.client_id) !== 1) {
                hasBookingOnlyBlock = true;
                reasons.push("Conductor restringido: Solo habilitado para servicios de Booking");
            }

            const isApt = reasons.length === 0;
            const rec = relevantRecs.find(r => r.driver_id === d.id);
            const isRecommended = Boolean(rec && isApt && !hasCapacityBlock && !hasBookingOnlyBlock);

            return {
                ...d,
                vehicle: v,
                vehicle_internal_code: v ? v.internal_code : '---',
                is_apt: isApt,
                has_capacity_block: hasCapacityBlock,
                has_booking_only_block: hasBookingOnlyBlock,
                has_hard_block: hasCapacityBlock || hasBookingOnlyBlock,
                capacity_detail: capacityDetail,
                reasons: reasons,
                unavailability_reason: reasons.join(' • '),
                is_recommended: isRecommended,
                recommendation_badge: rec?.recommendation_badge || '⭐ Sugerido',
                trips_last_30d: rec?.trips_last_30d,
                score_rank: rec?.score_rank ?? 9999
            };
        });
    }, [drivers, vehicles, baseId, changeBaseFilter, selectedFilterBaseId, currentTrip, isProvider, providerId, recommendedDrivers]);

    // Filtrar por término de búsqueda y por modo excepcional
    const filteredDrivers = useMemo(() => {
        let list = evaluatedDrivers;
        const trimmedSearch = searchTerm.trim();

        // Si NO está activado el modo excepcional Y NO se está buscando por texto,
        // mostrar solo los aptos. Si el usuario busca por texto o activa excepcional, mostrar todos para que siempre encuentre al chofer.
        if (!allowExceptional && !trimmedSearch) {
            list = list.filter(d => d.is_apt);
        }

        const sortDrivers = (arr) => {
            return [...arr].sort((a, b) => {
                // 1. Bloqueos duros (capacidad o solo booking) al fondo
                const aHard = a.has_capacity_block || a.has_booking_only_block;
                const bHard = b.has_capacity_block || b.has_booking_only_block;
                if (aHard && !bHard) return 1;
                if (!aHard && bHard) return -1;

                // 2. Aptos antes que no aptos
                if (a.is_apt && !b.is_apt) return -1;
                if (!a.is_apt && b.is_apt) return 1;

                // 3. Dentro de aptos, recomendados para el score primero
                if (a.is_apt && b.is_apt) {
                    if (a.is_recommended && !b.is_recommended) return -1;
                    if (!a.is_recommended && b.is_recommended) return 1;
                    if (a.is_recommended && b.is_recommended) {
                        return (a.score_rank || 0) - (b.score_rank || 0);
                    }
                }

                return (a.full_name || '').localeCompare(b.full_name || '');
            });
        };

        if (!trimmedSearch) {
            return sortDrivers(list);
        }

        const lower = trimmedSearch.toLowerCase();
        const cleanSearch = trimmedSearch.replace(/[^0-9]/g, '');

        const filtered = list.filter(d => {
            const matchesName = d.full_name?.toLowerCase().includes(lower);
            const matchesRut = lower ? d.rut?.toLowerCase().includes(lower) : false;
            
            let matchesPhone = false;
            if (cleanSearch.length > 0 && d.phone) {
                let cleanPhone = d.phone.replace(/[^0-9]/g, '');
                if (cleanPhone.startsWith('56')) {
                    cleanPhone = cleanPhone.substring(2);
                }
                matchesPhone = cleanPhone.includes(cleanSearch);
            }

            const matchesVehicle = d.vehicle && String(d.vehicle.internal_code).toLowerCase().includes(lower);
            const matchesPlate = d.vehicle?.plate && d.vehicle.plate.toLowerCase().includes(lower);
            
            return matchesName || matchesRut || matchesPhone || matchesVehicle || matchesPlate;
        });

        return sortDrivers(filtered);
    }, [evaluatedDrivers, allowExceptional, searchTerm]);

    const getDriverStatus = (driver) => {
        if (!driver.has_active_session) return 'offline';
        if (!driver.last_heartbeat) return 'offline';
        
        const heartbeatTime = new Date(driver.last_heartbeat).getTime();
        const now = new Date().getTime();
        const diffMinutes = (now - heartbeatTime) / 60000;
        
        if (diffMinutes > 5) {
            return 'offline';
        }
        
        return driver.is_online ? 'online' : 'paused';
    };

    const handleDriverClick = (driver) => {
        if (driver.has_capacity_block) {
            alert(`🚫 Asignación Imposible:\nEl móvil ${driver.vehicle_internal_code} (${driver.vehicle?.vehicle_type?.name || 'Vehículo'}) tiene capacidad para ${driver.vehicle?.vehicle_type?.capacity || 4} pasajeros, pero este servicio requiere ${driver.capacity_detail}.\n\nNo es posible asignarlo bajo ninguna circunstancia.`);
            return;
        }

        if (driver.has_booking_only_block) {
            alert(`⛔ CONDUCTOR RESTRINGIDO:\nNo puedes asignar a ${driver.full_name}.\nEste conductor está configurado con restricción 'Solo Booking' y este servicio pertenece a un convenio corporativo.\n\nSolo puede realizar servicios de Booking.com.`);
            return;
        }

        const conflictList = driverConflictsMap.get(driver.id) || [];
        if (conflictList.length > 0) {
            const detailLines = conflictList.map(
                (c) => `• Reserva #${c.trip_id} (${c.scheduled_time} hrs) — ${c.passenger_name || 'Pasajero'}\n  ${c.conflict_reason || ''}`
            ).join('\n');
            if (!window.confirm(`⚠️ ¡ATENCIÓN: CONDUCTOR CON RESERVAS MUY CERCANAS!\n\nEl conductor ${driver.full_name} ya tiene ${conflictList.length} servicio(s) cercano(s):\n\n${detailLines}\n\n¿Deseas asignarlo de todas formas?`)) {
                return;
            }
        }

        // Si el conductor presenta restricciones administrativas/operativas
        if (!driver.is_apt) {
            setExceptionalPromptDriver(driver);
            return;
        }

        onAssign(driver.id, false);
    };

    return (
        <>
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="bg-danger text-white">
                <Modal.Title className="fs-6 fw-bold">
                    Asignar Conductor {baseId ? '(Zona Específica)' : '(Con Auto)'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-3">
                <div className="mb-3 p-2.5 bg-light rounded border border-secondary border-opacity-10">
                    <Row className="g-2 align-items-center">
                        <Col md={6}>
                            <Form.Check 
                                type="checkbox"
                                id="change-base-checkbox"
                                label="🔍 Buscar conductores de otra base"
                                checked={changeBaseFilter}
                                onChange={(e) => {
                                    setChangeBaseFilter(e.target.checked);
                                    if (e.target.checked && !selectedFilterBaseId) {
                                        setSelectedFilterBaseId(baseId || '');
                                    }
                                }}
                                className="fw-bold small text-dark"
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Check 
                                type="checkbox"
                                id="allow-exceptional-checkbox"
                                label={
                                    <span className="text-danger fw-bold small d-inline-flex align-items-center gap-1">
                                        <Unlock size={14} /> Asignación Excepcional (Ver con advertencias)
                                    </span>
                                }
                                checked={allowExceptional}
                                onChange={(e) => setAllowExceptional(e.target.checked)}
                            />
                        </Col>
                    </Row>

                    {changeBaseFilter && (
                        <Form.Group as={Row} className="align-items-center g-2 mt-2 pt-2 border-top">
                            <Form.Label column sm="4" className="small fw-semibold text-muted">Seleccionar Base:</Form.Label>
                            <Col sm="8">
                                <Form.Select 
                                    size="sm"
                                    value={selectedFilterBaseId}
                                    onChange={(e) => setSelectedFilterBaseId(e.target.value)}
                                >
                                    <option value="">-- Todas las bases --</option>
                                    {bases.map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Form.Group>
                    )}

                    {currentTrip && (
                        <div className="small text-muted mt-2 pt-2 border-top d-flex justify-content-between align-items-center">
                            <span>Pasajeros requeridos: <strong>{currentTrip.passenger_count || 1} pax</strong></span>
                            {allowExceptional && (
                                <Badge bg="warning" text="dark" className="small">
                                    Modo Excepcional Activo
                                </Badge>
                            )}
                        </div>
                    )}
                </div>

                {recommendedDrivers.length > 0 && (
                    <div className="d-flex align-items-center justify-content-between p-2 mb-3 rounded bg-warning bg-opacity-10 border border-warning-subtle text-dark small">
                        <div className="d-flex align-items-center gap-1.5">
                            <span className="text-warning">⭐</span>
                            <span><strong>Asistente de Equidad:</strong> Conductores sugeridos al inicio para equilibrar tu score.</span>
                        </div>
                        <Badge bg="warning" text="dark" className="border border-warning">
                            {recommendedDrivers.length} sugeridos
                        </Badge>
                    </div>
                )}

                <Form.Control 
                    type="text" 
                    placeholder="🔍 Escribe nombre, RUT, teléfono o número de móvil..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mb-3"
                    autoFocus
                />

                <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    <ListGroup variant="flush">
                        {filteredDrivers.map(d => {
                            const conflictList = driverConflictsMap.get(d.id) || [];
                            const hasConflict = conflictList.length > 0;
                            return (
                                <ListGroup.Item 
                                    key={d.id} 
                                    action={!d.has_hard_block} 
                                    onClick={() => handleDriverClick(d)}
                                    style={d.has_hard_block ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                                    className={`d-flex justify-content-between align-items-center py-2.5 px-3 border rounded mb-1.5 ${
                                        d.has_hard_block 
                                            ? 'bg-danger bg-opacity-10 border-danger-subtle' 
                                            : hasConflict
                                                ? 'bg-danger bg-opacity-10 border-danger'
                                                : !d.is_apt 
                                                    ? 'bg-warning bg-opacity-10 border-warning-subtle' 
                                                    : d.is_recommended
                                                        ? 'border-warning bg-warning bg-opacity-10'
                                                        : ''
                                    }`}
                                >
                                    <div className="d-flex align-items-start gap-2.5" style={{ minWidth: 0 }}>
                                        <div className="mt-0.5">
                                            {d.has_capacity_block ? (
                                                <OverlayTrigger placement="top" overlay={<Tooltip>BLOQUEO FÍSICO: Capacidad insuficiente ({d.capacity_detail}). No se puede asignar.</Tooltip>}>
                                                    <Ban size={18} className="text-danger" />
                                                </OverlayTrigger>
                                            ) : d.has_booking_only_block ? (
                                                <OverlayTrigger placement="top" overlay={<Tooltip>CONDUCTOR RESTRINGIDO: Solo habilitado para servicios de Booking. No se puede asignar.</Tooltip>}>
                                                    <Ban size={18} className="text-danger" />
                                                </OverlayTrigger>
                                            ) : hasConflict ? (
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Conflicto de Horario: Ya tiene {conflictList.length} reserva(s) muy cercana(s).</Tooltip>}>
                                                    <AlertTriangle size={18} className="text-danger" />
                                                </OverlayTrigger>
                                            ) : !d.is_apt ? (
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Asignación Excepcional: {d.unavailability_reason}</Tooltip>}>
                                                    <AlertTriangle size={18} className="text-warning" />
                                                </OverlayTrigger>
                                            ) : d.is_recommended ? (
                                                <OverlayTrigger placement="top" overlay={<Tooltip>⭐ Sugerido para tu Score de Equidad ({d.trips_last_30d ?? 0} viajes en 30 días)</Tooltip>}>
                                                    <span style={{ fontSize: '18px', lineHeight: 1 }}>⭐</span>
                                                </OverlayTrigger>
                                            ) : (
                                                <CheckCircle size={18} className="text-success" />
                                            )}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <span className={`fw-bold ${d.has_hard_block ? 'text-danger text-decoration-line-through' : hasConflict ? 'text-danger' : 'text-dark'}`}>
                                                    {d.full_name}
                                                </span>
                                                <Badge bg="dark" className="small">
                                                    Móvil: {d.vehicle_internal_code}
                                                </Badge>
                                                {hasConflict && (
                                                    <Badge bg="danger" className="small">
                                                        ⚠️ Reserva Cercana ({conflictList.map(c => `#${c.trip_id}`).join(', ')})
                                                    </Badge>
                                                )}
                                                {d.is_recommended && (
                                                    <Badge bg="warning" text="dark" className="border border-warning small">
                                                        ⭐ {d.recommendation_badge || 'Sugerido'} ({d.trips_last_30d ?? 0} v. en 30d)
                                                    </Badge>
                                                )}
                                                {d.vehicle?.vehicle_type?.capacity && (
                                                    <Badge bg={d.has_capacity_block ? 'danger' : 'secondary'} className="small">
                                                        {d.vehicle.vehicle_type.capacity} pax
                                                    </Badge>
                                                )}
                                                {d.has_capacity_block && (
                                                    <Badge bg="danger" className="small">
                                                        🚫 Capacidad Insuficiente
                                                    </Badge>
                                                )}
                                                {d.has_booking_only_block && (
                                                    <Badge bg="danger" className="small">
                                                        🚫 Solo Booking
                                                    </Badge>
                                                )}
                                            </div>
                                            
                                            {/* Advertencias o restricciones */}
                                            {d.has_capacity_block ? (
                                                <div className="text-danger small fw-bold mt-0.5" style={{ fontSize: '11px' }}>
                                                    🚫 BLOQUEADO: Capacidad física insuficiente ({d.capacity_detail}) - No se puede asignar
                                                </div>
                                            ) : d.has_booking_only_block ? (
                                                <div className="text-danger small fw-bold mt-0.5" style={{ fontSize: '11px' }}>
                                                    🚫 BLOQUEADO: Conductor restringido exclusivamente a servicios Booking.com
                                                </div>
                                            ) : null}
                                            {hasConflict && (
                                                <div className="text-danger small fw-bold mt-0.5" style={{ fontSize: '11px' }}>
                                                    {conflictList.map((c, ci) => (
                                                        <div key={ci}>
                                                            ⚠️ Choca con Reserva #{c.trip_id} ({c.scheduled_time} hrs — {c.passenger_name || 'Pasajero'})
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {!d.has_capacity_block && !d.has_booking_only_block && !d.is_apt ? (
                                                <div className="text-danger small fw-semibold mt-0.5" style={{ fontSize: '11px' }}>
                                                    ⚠️ {d.unavailability_reason}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0 ms-2">
                                        <Badge bg={d.has_active_session ? 'success' : 'secondary'} style={{ fontSize: '0.72em' }}>
                                            {d.has_active_session ? 'Conectado' : 'Sin Sesión'}
                                        </Badge>
                                        {(() => {
                                            const status = getDriverStatus(d);
                                            return (
                                                <Badge bg={status === 'online' ? 'success' : 'danger'} style={{ fontSize: '0.68em' }}>
                                                    {status === 'online' ? '🟢 Disponible' : '🔴 No Disponible'}
                                                </Badge>
                                            );
                                        })()}
                                    </div>
                                </ListGroup.Item>
                            );
                        })}
                        {filteredDrivers.length === 0 && (
                            <div className="text-center text-muted p-4">
                                <p className="mb-1">No se encontraron conductores con los filtros actuales.</p>
                                {!allowExceptional && (
                                    <Button 
                                        variant="outline-warning" 
                                        size="sm" 
                                        className="mt-2 fw-bold text-dark"
                                        onClick={() => setAllowExceptional(true)}
                                    >
                                        <Unlock size={14} className="me-1" /> Activar Asignación Excepcional
                                    </Button>
                                )}
                            </div>
                        )}
                    </ListGroup>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide}>Cancelar</Button>
            </Modal.Footer>
        </Modal>

        {/* MODAL DE CONFIRMACIÓN DE ASIGNACIÓN EXCEPCIONAL */}
        {exceptionalPromptDriver && (
            <Modal 
                show={true} 
                onHide={() => setExceptionalPromptDriver(null)} 
                centered 
                size="md"
                style={{ zIndex: 1150 }}
            >
                <Modal.Header closeButton className="bg-warning-subtle border-bottom border-warning">
                    <Modal.Title className="fs-6 fw-bold text-dark d-flex align-items-center gap-2">
                        <AlertTriangle className="text-warning" size={20} />
                        Confirmar Asignación Excepcional
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3">
                    <p className="mb-2">
                        El conductor <strong>{exceptionalPromptDriver.full_name}</strong> {exceptionalPromptDriver.vehicle_internal_code !== '---' && `[Móvil ${exceptionalPromptDriver.vehicle_internal_code}]`} presenta las siguientes restricciones para este servicio:
                    </p>
                    <div className="bg-light p-2.5 rounded border border-warning-subtle mb-3">
                        <ul className="mb-0 ps-3 text-danger small">
                            {exceptionalPromptDriver.reasons && exceptionalPromptDriver.reasons.length > 0 ? (
                                exceptionalPromptDriver.reasons.map((r, idx) => (
                                    <li key={idx} className="fw-semibold mb-0.5">{r}</li>
                                ))
                            ) : (
                                <li className="fw-semibold mb-0.5">{exceptionalPromptDriver.unavailability_reason}</li>
                            )}
                        </ul>
                    </div>
                    <Alert variant="warning" className="small mb-0 py-2">
                        ¿Deseas forzar la asignación de este conductor de forma extraordinaria? Esta acción quedará registrada en la auditoría del servicio.
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="bg-light p-2 d-flex justify-content-end gap-2">
                    <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={() => setExceptionalPromptDriver(null)}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        variant="warning" 
                        size="sm" 
                        className="fw-bold text-dark"
                        onClick={() => {
                            const driverId = exceptionalPromptDriver.id;
                            setExceptionalPromptDriver(null);
                            onAssign(driverId, true);
                        }}
                    >
                        Confirmar Asignación Excepcional
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        </>
    );
};

export default DriverAssignModal;